import React from "react";
import { motion, type Variants } from "framer-motion";

interface PageHeroProps {
  badge: string;
  badgeDotColor?: string;
  title: string;
  subtitle?: string;
  description: string;
  maxWidth?: string;
  children?: React.ReactNode;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function PageHero({
  badge,
  badgeDotColor = "bg-primary/80",
  title,
  subtitle,
  description,
  maxWidth = "max-w-4xl",
  children,
}: PageHeroProps) {
  return (
    <section className="no-print relative bg-gradient-to-br from-brand-dark via-brand to-brand-dark pt-26 pb-16 sm:pt-30 sm:pb-20 overflow-hidden text-white">
      {/* Ambient Lighting Glow */}
      <motion.div
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.12, 0.22, 0.12],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/20 rounded-full blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 50%, #39b7e9 0%, transparent 50%), radial-gradient(circle at 75% 50%, #39b7e9 0%, transparent 50%)",
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className={`relative ${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center`}
      >
        {/* Badge */}
        <motion.div
          variants={itemVariants}
          className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6 backdrop-blur-xs"
        >
          <span className={`w-2 h-2 ${badgeDotColor} rounded-full animate-pulse`} />
          <span className="text-slate-300 text-xs font-semibold tracking-wide uppercase">
            {badge}
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          variants={itemVariants}
          className="text-4xl sm:text-5xl lg:text-6xl font-kordher text-white tracking-widest leading-tight"
        >
          {title}
        </motion.h1>

        {/* Subtitle */}
        {subtitle && (
          <motion.p
            variants={itemVariants}
            className="mt-4 text-base sm:text-lg font-nexa uppercase tracking-widest text-brand-light"
          >
            {subtitle}
          </motion.p>
        )}

        {/* Description */}
        <motion.p
          variants={itemVariants}
          className="mt-4 text-base sm:text-lg text-slate-300 font-light max-w-3xl mx-auto leading-relaxed"
        >
          {description}
        </motion.p>

        {/* Optional Action Buttons or Filter Links */}
        {children && (
          <motion.div variants={itemVariants} className="mt-8 flex flex-wrap justify-center gap-3 text-xs text-slate-200">
            {children}
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
